/**
 * The value of `release.heads-up-intro` (EN-10748): a JSON flag whose variation IS the
 * announcement, exactly as `release.multi-run-intro` is for multi-run (EN-10791). One
 * edition at launch, `heads-up-launch`; a later change to the format (Omaha heads-up, a
 * bigger Sit & Go) is a new variation with its own seen key and no deploy.
 *
 * Parsed here, at the flag boundary, so feature code only ever sees a validated config
 * or null. The rules are multiRunIntroConfig's:
 *   - An empty object is off (the `off` variation; LaunchDarkly refuses a null JSON
 *     variation). null and false are off too.
 *   - A payload without a valid `edition` is off, and is reported.
 *   - Every other field is optional and falls back to the timeless copy in
 *     components/HeadsUpIntro/headsUpIntroCopy.ts. A typo in LaunchDarkly degrades to
 *     true, generic copy, never to a half-built modal.
 *   - Lengths are capped so no field can break the portrait layout (LIMITS).
 * Plus one field of its own: `tiers`, the buy-ins as the Sit & Go tab labels its sections
 * with and the prize each one plays for, shown as a ladder so a player recognises the
 * sections in the lobby and sees what winning is worth.
 */
export type HeadsUpIntroTier = { buyIn: string; prize: string };

export type HeadsUpIntroConfig = {
  /** Names the seen key, the analytics events and the Sentry path. */
  edition: string;
  /** The headline as its two lines: what the game is, then what you play for. */
  titleLines?: readonly [string, string];
  /** The ladder: each buy-in as the lobby labels it ("$6") and what the winner takes ("$10"). */
  tiers?: readonly HeadsUpIntroTier[];
  /** One to three lines under the "How it works" card. */
  availability?: readonly string[];
  /** The fine print under the action. */
  smallPrint?: string;
};

export type HeadsUpIntroConfigField = keyof HeadsUpIntroConfig;

export type HeadsUpIntroConfigIssue = { field: HeadsUpIntroConfigField; reason: string };

/** Lower-case letters, digits and dashes, starting with a letter or digit (`heads-up-launch`). */
export const HEADS_UP_INTRO_EDITION_PATTERN = /^[a-z0-9][a-z0-9-]{0,39}$/;

/**
 * The longest text each field may carry, in characters. The launch copy sits well inside
 * them (headline lines of 23 and 21, availability lines under 80, fine print under 240,
 * amounts of at most 6). Five ladder rows of 8-character amounts fit at 390 px.
 */
export const HEADS_UP_INTRO_LIMITS = {
  titleLine: 28,
  amount: 8,
  tiers: 5,
  availabilityLine: 80,
  availabilityLines: 3,
  smallPrint: 240
} as const;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A trimmed, non-empty string within `max`, or null. */
const readLine = (value: unknown, max: number): string | null => {
  if (typeof value !== 'string') return null;
  const line = value.trim();
  return line.length > 0 && line.length <= max ? line : null;
};

/** Every row a {buyIn, prize} pair of short amounts, or null. */
const readTiers = (value: unknown): HeadsUpIntroTier[] | null => {
  if (!Array.isArray(value) || value.length < 1 || value.length > HEADS_UP_INTRO_LIMITS.tiers)
    return null;
  const tiers = value.map((row) => {
    if (!isPlainObject(row)) return null;
    const buyIn = readLine(row.buyIn, HEADS_UP_INTRO_LIMITS.amount);
    const prize = readLine(row.prize, HEADS_UP_INTRO_LIMITS.amount);
    return buyIn && prize ? { buyIn, prize } : null;
  });
  return tiers.every((tier): tier is HeadsUpIntroTier => tier !== null) ? tiers : null;
};

const readLines = (value: unknown, count: { min: number; max: number }, maxLength: number) => {
  if (!Array.isArray(value) || value.length < count.min || value.length > count.max) return null;
  const lines = value.map((line) => readLine(line, maxLength));
  return lines.every((line): line is string => line !== null) ? lines : null;
};

export const parseHeadsUpIntroConfig = (
  raw: unknown,
  report: (issue: HeadsUpIntroConfigIssue) => void = () => undefined
): HeadsUpIntroConfig | null => {
  if (raw === null || raw === undefined || raw === false) return null;
  if (!isPlainObject(raw)) {
    report({ field: 'edition', reason: 'the payload is not an object' });
    return null;
  }
  // The `off` variation: nothing to announce, and not a mistake to report.
  if (Object.keys(raw).length === 0) return null;

  const edition = typeof raw.edition === 'string' ? raw.edition.trim() : '';
  if (!HEADS_UP_INTRO_EDITION_PATTERN.test(edition)) {
    report({ field: 'edition', reason: 'missing, or not lower-case letters, digits and dashes' });
    return null;
  }

  let titleLines: [string, string] | undefined;
  if (raw.titleLines !== undefined) {
    const lines = readLines(raw.titleLines, { min: 2, max: 2 }, HEADS_UP_INTRO_LIMITS.titleLine);
    if (lines) titleLines = [lines[0], lines[1]];
    else
      report({
        field: 'titleLines',
        reason: `expected exactly 2 lines of 1-${HEADS_UP_INTRO_LIMITS.titleLine} characters`
      });
  }

  let tiers: HeadsUpIntroTier[] | undefined;
  if (raw.tiers !== undefined) {
    const parsed = readTiers(raw.tiers);
    if (parsed) tiers = parsed;
    else
      report({
        field: 'tiers',
        reason: `expected 1-${HEADS_UP_INTRO_LIMITS.tiers} rows of {buyIn, prize}, each 1-${HEADS_UP_INTRO_LIMITS.amount} characters`
      });
  }

  let availability: string[] | undefined;
  if (raw.availability !== undefined) {
    const lines = readLines(
      raw.availability,
      { min: 1, max: HEADS_UP_INTRO_LIMITS.availabilityLines },
      HEADS_UP_INTRO_LIMITS.availabilityLine
    );
    if (lines) availability = lines;
    else
      report({
        field: 'availability',
        reason: `expected 1-${HEADS_UP_INTRO_LIMITS.availabilityLines} lines of 1-${HEADS_UP_INTRO_LIMITS.availabilityLine} characters`
      });
  }

  let smallPrint: string | undefined;
  if (raw.smallPrint !== undefined) {
    const text = readLine(raw.smallPrint, HEADS_UP_INTRO_LIMITS.smallPrint);
    if (text) smallPrint = text;
    else
      report({
        field: 'smallPrint',
        reason: `expected 1-${HEADS_UP_INTRO_LIMITS.smallPrint} characters`
      });
  }

  return {
    edition,
    ...(titleLines ? { titleLines } : {}),
    ...(tiers ? { tiers } : {}),
    ...(availability ? { availability } : {}),
    ...(smallPrint ? { smallPrint } : {})
  };
};
